import { useState } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { Route } from '@/routes/_public/login';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@/lib/zod-resolver';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { signIn, authClient } from '@/lib/auth-client';
import { GOOGLE_CLIENT_ID } from '@/lib/env';
import { GoogleIcon } from '@/components/icons/google-icon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function Login(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchParams = Route.useSearch();
  const resetSuccess = searchParams.reset === 'success';
  const signupSuccess = searchParams.signup === 'success';
  const verifiedSuccess = searchParams.verified === 'success';
  const oauthError = searchParams.error;
  const oauthErrorMessage =
    oauthError === 'signup_disabled'
      ? 'No account found. Please sign up first.'
      : oauthError
        ? 'Google sign in failed. Please try again.'
        : null;
  const [error, setError] = useState<string | null>(oauthErrorMessage);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const handleGoogleSignIn = async (): Promise<void> => {
    setIsGoogleLoading(true);
    setError(null);
    try {
      sessionStorage.removeItem('twoFactorVerified');
      const redirectTo = searchParams.redirect ?? '/';
      await signIn.social({
        provider: 'google',
        callbackURL: window.location.origin + redirectTo,
        errorCallbackURL: window.location.origin + '/login',
      });
    } catch {
      setError('Google sign in failed');
      setIsGoogleLoading(false);
    }
  };

  const onSubmit = async (data: LoginForm): Promise<void> => {
    setIsLoading(true);
    setError(null);
    sessionStorage.removeItem('twoFactorVerified');

    try {
      const result = await signIn.email({
        email: data.email,
        password: data.password,
      });

      if (result.error) {
        setError(result.error.message || 'Sign in failed');
        return;
      }

      // Refetch session to update auth state before navigating
      await authClient.getSession({ fetchOptions: { credentials: 'include' } });
      // Invalidate all queries to refetch with new session
      await queryClient.invalidateQueries();
      // Redirect to the page user was on before session expired, or dashboard
      const redirectTo = searchParams.redirect ?? '/';
      navigate({ to: redirectTo });
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border-0 shadow-lg">
        <CardHeader className="space-y-2 pb-1">
          <p className="text-sm font-semibold tracking-wide text-muted-foreground">VagusX</p>
          <CardTitle className="text-2xl font-bold">Sign in</CardTitle>
          <CardDescription>
            Enter your email and password to sign in to your account
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-5">
            {signupSuccess && (
              <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800">
                Check your email to verify your account before signing in.
              </div>
            )}
            {verifiedSuccess && (
              <div className="rounded-md bg-green-100 p-3 text-sm text-green-800">
                Email verified! Sign in to get started.
              </div>
            )}
            {resetSuccess && (
              <div className="rounded-md bg-green-100 p-3 text-sm text-green-800">
                Password reset successful. Please sign in with your new password.
              </div>
            )}
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                {...register('email')}
                disabled={isLoading}
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-muted-foreground hover:text-primary"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  {...register('password')}
                  disabled={isLoading}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
            {GOOGLE_CLIENT_ID && (
              <div className="space-y-3 pt-1">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleSignIn}
                  disabled={isGoogleLoading}
                >
                  <GoogleIcon className="mr-2 h-4 w-4" />
                  {isGoogleLoading ? 'Redirecting...' : 'Continue with Google'}
                </Button>
              </div>
            )}
          </CardContent>
          <CardFooter className="justify-center pt-2">
            <p className="text-sm text-muted-foreground">
              Don&apos;t have an account?{' '}
              <Link to="/signup" className="text-primary hover:underline">
                Sign up
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
